'use client'

import { useEffect, useRef } from 'react'
import ClipboardJS from 'clipboard'

interface UseCopyToClipboardReturn {
  buttonRef: React.RefObject<HTMLButtonElement>
  copyToClipboard?: () => Promise<void>
}

interface UseCopyToClipboardOptions {
  useNativeClipboard?: boolean
  text: string // Markdown-formatted text to copy
}

const nativeCopy = async (markdownText: string) => {
  if (!markdownText) return

  const item = new window.ClipboardItem({
    'text/plain': new Blob([markdownText], { type: 'text/plain' }),
  })
  await navigator.clipboard.write([item])
}

export const useCopyToClipboard = (
  options: UseCopyToClipboardOptions = {
    useNativeClipboard: false,
    text: '',
  }
): UseCopyToClipboardReturn => {
  const { useNativeClipboard, text } = options
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!buttonRef.current || useNativeClipboard || !text) return

    const clipboard = new ClipboardJS(buttonRef.current, {
      text: () => text,
    })

    return () => {
      clipboard.destroy()
    }
  }, [useNativeClipboard, text])

  const copyToClipboard = async () => {
    if (useNativeClipboard) {
      await nativeCopy(text)
    }
  }

  return {
    copyToClipboard,
    buttonRef,
  }
}
